<template>
  <div class="ul">
    <div class="ul__panel">
      <!-- 1 Bild -->
      <div class="ul__card">
        <div class="ul__head"><span class="ul__step">1</span><b>Artwork</b><span class="ul__muted">das Bild, das hochgerechnet werden soll</span></div>
        <div class="ul__drop" :class="{ 'ul__drop--over': dragArt }" @dragover.prevent="dragArt = true" @dragleave="dragArt = false" @drop.prevent="(e) => onDrop(e, 'artwork')" @click="$refs.art.click()">
          <input ref="art" type="file" accept="image/*" style="display: none" @change="(e) => onPick(e, 'artwork')" />
          <img v-if="artwork" :src="artwork.preview" alt="" class="ul__artPreview" />
          <template v-else><v-icon large color="rgba(0,0,0,.35)">mdi-image-plus</v-icon><div class="ul__muted">Klicken oder Artwork hierher ziehen</div></template>
        </div>
      </div>

      <!-- 2 Fotos (für Score + Detail-Pass) -->
      <div class="ul__card">
        <div class="ul__head"><span class="ul__step">2</span><b>Originalfotos</b><span class="ul__muted">optional, nötig für Prüfung und Detail-Pass</span></div>
        <!-- Fotos auch per Drag & Drop auf die ganze Fläche (bis 4) -->
        <div class="ul__thumbs ul__thumbs--drop" :class="{ 'ul__drop--over': dragPhoto }" @dragover.prevent="dragPhoto = true" @dragleave="dragPhoto = false" @drop.prevent="(e) => onDrop(e, 'photo')">
          <div v-for="p in photos" :key="p.id" class="ul__thumb"><img :src="p.preview" alt="" /><button type="button" class="ul__thumbX" @click="removePhoto(p.id)">×</button></div>
          <button v-if="photos.length < 4" type="button" class="ul__thumbAdd" title="Klicken oder Fotos hierher ziehen" @click="$refs.photo.click()"><v-icon>mdi-plus</v-icon></button>
          <span v-if="!photos.length" class="ul__muted ul__dropHint">oder Fotos hierher ziehen</span>
          <input ref="photo" type="file" accept="image/*" multiple style="display: none" @change="(e) => onPick(e, 'photo')" />
        </div>
      </div>

      <!-- 3 Druckprofil -->
      <div class="ul__card">
        <div class="ul__head"><span class="ul__step">3</span><b>Druckprofil</b><span class="ul__muted">Einstellungen der Stil-Version wie im Bestellweg</span></div>
        <div class="ul__row">
          <v-select v-model="profileKey" :items="profileItems" dense outlined hide-details label="Stil-Version" class="ul__select" />
        </div>
        <div v-if="profile" class="ul__chips">
          <span class="ul__chip">Upscale {{ profile.print.upscale && profile.print.upscale.engine }}</span>
          <span v-if="profile.print.detailPass && profile.print.detailPass.enabled" class="ul__chip">Detail-Pass {{ profile.print.detailPass.engine }}</span>
          <span v-if="profile.transparent" class="ul__chip">Freisteller {{ (profile.print.cutout && profile.print.cutout.method) || "dual" }}</span>
        </div>
      </div>

      <!-- 4 Schritte -->
      <div class="ul__card">
        <div class="ul__head"><span class="ul__step">4</span><b>Schritte</b></div>
        <div class="ul__steps">
          <div class="ul__stepRow">
            <v-switch v-model="steps.upscale" dense hide-details inset label="Upscale" class="ul__switch" />
            <template v-if="steps.upscale">
              <v-select v-model="engine" :items="engineItems" dense outlined hide-details label="Engine" class="ul__select ul__select--s" />
              <div class="ul__seg">
                <button v-for="r in ['2K', '4K']" :key="r" type="button" :class="{ on: resolution === r }" @click="resolution = r">{{ r }}</button>
              </div>
            </template>
          </div>
          <div class="ul__stepRow">
            <v-switch v-model="steps.score" dense hide-details inset label="Prüfung (Score)" class="ul__switch" :disabled="!photos.length || steps.detailPass" />
            <span v-if="!photos.length" class="ul__muted">braucht ein Originalfoto</span>
          </div>
          <div class="ul__stepRow">
            <v-switch v-model="steps.detailPass" dense hide-details inset label="Detail-Pass (Gesichter, Schrift)" class="ul__switch" :disabled="!photos.length" />
            <span v-if="steps.detailPass" class="ul__muted">schaltet die Prüfung mit ein</span>
          </div>
          <div class="ul__stepRow">
            <v-switch v-model="steps.cutout" dense hide-details inset label="Freistellen" class="ul__switch" :disabled="!profile || !profile.transparent" />
            <span v-if="profile && !profile.transparent" class="ul__muted">nur bei Freisteller-Versionen</span>
          </div>
        </div>
      </div>

      <v-btn color="primary" depressed rounded block large :disabled="!canStart" :loading="starting" @click="start">
        <v-icon left>mdi-arrow-expand-all</v-icon>Upscale starten
      </v-btn>
      <div v-if="error" class="ul__error mt-2">{{ error }}</div>
      <div class="ul__muted mt-2">Läuft als Lab-Job wie in artrefine: kein Task, kein Auto-Import. Ergebnisse liegen einen Tag im Speicher, bitte bei Bedarf herunterladen.</div>
    </div>

    <!-- Läufe -->
    <div class="ul__results">
      <div v-if="!runs.length" class="ul__empty"><v-icon large color="rgba(0,0,0,.25)">mdi-arrow-expand-all</v-icon><div class="ul__muted">Noch keine Upscales in dieser Sitzung.</div></div>
      <div v-for="r in runs" :key="r.jobId" class="ul__result">
        <div class="ul__resultHead">
          <b>{{ r.label }}</b>
          <span class="ul__chip" v-for="s in r.stepsLabel" :key="s">{{ s }}</span>
          <v-spacer />
          <span class="ul__muted ul__mono">Job {{ r.jobId }}</span>
          <button type="button" class="ul__iconBtn" title="Aus der Liste entfernen" @click="removeRun(r.jobId)"><v-icon small>mdi-close</v-icon></button>
        </div>
        <UpscaleProgress :job-id="r.jobId" class="mt-2" />
        <UpscaleJobResult :job-id="r.jobId" />
      </div>
    </div>
  </div>
</template>

<script>
// Upscale ohne Generierung (Malte 16.10.): beliebiges Artwork hochladen, Druckprofil einer Stil-Version wählen,
// die vier Schritte wie in artrefine ein-/ausschalten. Start über firebase_aq startLabUpscale (Quelle 'lab').
import { httpsCallable } from "firebase/functions";
import { getDownloadURL, getStorage, ref as storageRef, uploadBytes } from "firebase/storage";
import { functions as mainFunctions } from "../../../fb";
import UpscaleProgress from "@/components/Upscales/UpscaleProgress.vue";
import UpscaleJobResult from "@/components/Upscales/UpscaleJobResult";
import { generateActivity, trackUpscaleJob } from "@/generateRuns";

// gleicher Schlüssel in AllOrders/components/CustomerArtworkDialog.vue (Läufe erscheinen auch hier)
const RUNS_KEY = "taskdash_upscale_lab_runs_v1";
const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

export default {
  name: "UpscaleLabPanel",
  components: { UpscaleProgress, UpscaleJobResult },
  props: {
    /** getStyleMeta-Stile aus GenerateView (perVersion[].print = Druckprofil) */
    styles: { type: Array, default: () => [] },
  },
  data() {
    let runs = [];
    try {
      runs = JSON.parse(localStorage.getItem(RUNS_KEY) || "[]");
    } catch (e) {
      runs = [];
    }
    return {
      artwork: null,
      photos: [],
      dragArt: false,
      dragPhoto: false,
      profileKey: "",
      // Prüfung + Detail-Pass erst an, wenn ein Originalfoto da ist (addFiles)
      steps: { upscale: true, score: false, detailPass: false, cutout: false },
      engine: "default",
      resolution: "4K",
      starting: false,
      error: "",
      runs: Array.isArray(runs) ? runs : [],
    };
  },
  computed: {
    profiles() {
      const out = [];
      for (const s of this.styles) for (const v of s.perVersion || []) if (v.print) out.push({ key: `${s.id}|${v.version}`, styleId: s.id, version: v.version, print: v.print, transparent: !!v.transparent });
      return out;
    },
    profileItems() {
      return this.profiles.map((p) => ({ text: `${p.styleId} v${p.version}${p.transparent ? " · Freisteller" : ""}`, value: p.key }));
    },
    profile() {
      return this.profiles.find((p) => p.key === this.profileKey) || null;
    },
    engineItems() {
      const std = this.profile && this.profile.print.upscale && this.profile.print.upscale.engine;
      return [
        { text: `Profil-Standard (${std || "–"})`, value: "default" },
        { text: "Gemini (gemini-nano-2)", value: "gemini-nano-2" },
        { text: "Sunburst (gpt-image-2.5-sunburst)", value: "gpt-image-2.5-sunburst" },
      ];
    },
    anyStep() {
      return this.steps.upscale || this.steps.score || this.steps.detailPass || this.steps.cutout;
    },
    /** neue Upscales von außerhalb (Kunden-Artwork-Dialog) → Liste neu aus dem Speicher lesen */
    upscaleActivityKey() {
      return generateActivity.items.filter((i) => i.type === "up").map((i) => i.id).join(",");
    },
    canStart() {
      return !!this.artwork && !!this.profile && this.anyStep && !this.starting;
    },
  },
  watch: {
    upscaleActivityKey() {
      try {
        const saved = JSON.parse(localStorage.getItem(RUNS_KEY) || "[]");
        if (Array.isArray(saved)) this.runs = saved;
      } catch (e) {
        /* kaputt → Liste bleibt */
      }
    },
    profiles: {
      immediate: true,
      handler(list) {
        if (!this.profileKey && list.length) {
          // Standard: rustic in der höchsten Version, sonst das erste Profil
          const rustic = list.filter((p) => p.styleId === "rustic").sort((a, b) => b.version - a.version)[0];
          this.profileKey = (rustic || list[0]).key;
        }
      },
    },
    profile(p) {
      this.steps.cutout = !!(p && p.transparent);
      this.steps.detailPass = !!(p && p.print.detailPass && p.print.detailPass.enabled) && this.photos.length > 0;
    },
    "photos.length"(n) {
      if (!n) {
        this.steps.score = false;
        this.steps.detailPass = false;
      }
    },
    "steps.detailPass"(on) {
      if (on) this.steps.score = true;
    },
  },
  methods: {
    onPick(e, kind) {
      this.addFiles([...(e.target.files || [])], kind);
      e.target.value = "";
    },
    onDrop(e, kind) {
      this.dragArt = false;
      this.dragPhoto = false;
      this.addFiles([...(e.dataTransfer.files || [])].filter((f) => f.type.startsWith("image/")), kind);
    },
    addFiles(files, kind) {
      if (kind === "artwork") {
        const f = files[0];
        if (f) this.artwork = { file: f, preview: URL.createObjectURL(f) };
        return;
      }
      const room = 4 - this.photos.length;
      files.slice(0, Math.max(0, room)).forEach((f) => this.photos.push({ id: uuid(), file: f, preview: URL.createObjectURL(f) }));
      if (this.photos.length && this.profile) {
        this.steps.score = true;
        this.steps.detailPass = !!(this.profile.print.detailPass && this.profile.print.detailPass.enabled);
      }
    },
    removePhoto(id) {
      this.photos = this.photos.filter((p) => p.id !== id);
    },
    async upload(file, name) {
      const ext = (file.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
      const sref = storageRef(getStorage(), `/mailAttachments/upscaleLabInputs/${Date.now()}_${uuid()}_${name}.${ext}`);
      const up = await uploadBytes(sref, file, { cacheControl: "public,max-age=3600", contentType: file.type || "image/png" });
      return getDownloadURL(up.ref);
    },
    async start() {
      if (!this.canStart) return;
      this.starting = true;
      this.error = "";
      try {
        const artworkUrl = await this.upload(this.artwork.file, "artwork");
        const photos = await Promise.all(this.photos.map((p, i) => this.upload(p.file, `photo${i + 1}`)));
        const p = this.profile;
        const call = httpsCallable(mainFunctions, "startLabUpscale", { timeout: 60000 });
        const { data } = await call({
          print: p.print,
          transparent: p.transparent && this.steps.cutout,
          styleId: p.styleId,
          version: p.version,
          artworkUrl,
          opaqueUrl: artworkUrl,
          photos,
          resolution: this.resolution,
          engine: this.engine === "default" ? null : this.engine,
          upscale: this.steps.upscale,
          score: this.steps.score,
          detailPass: this.steps.detailPass,
          cutout: p.transparent ? this.steps.cutout : false,
          label: `TaskDash Upscale ${p.styleId} v${p.version}`,
        });
        const stepsLabel = [this.steps.upscale && `Upscale ${this.resolution}`, this.steps.score && "Prüfung", this.steps.detailPass && "Detail-Pass", this.steps.cutout && "Freisteller"].filter(Boolean);
        this.runs = [{ jobId: data.jobId, label: `${p.styleId} v${p.version}`, stepsLabel, at: Date.now() }, ...this.runs].slice(0, 20);
        trackUpscaleJob(data.jobId, `Upscale ${p.styleId} v${p.version}`);
        localStorage.setItem(RUNS_KEY, JSON.stringify(this.runs));
      } catch (e) {
        this.error = e.message || String(e);
      } finally {
        this.starting = false;
      }
    },
    removeRun(jobId) {
      this.runs = this.runs.filter((r) => r.jobId !== jobId);
      localStorage.setItem(RUNS_KEY, JSON.stringify(this.runs));
    },
  },
};
</script>

<style lang="scss" scoped>
$shadow: 0 1px 2px rgba(16, 24, 40, 0.06), 0 4px 14px rgba(16, 24, 40, 0.07);
.ul {
  display: grid; grid-template-columns: 400px 1fr; gap: 14px; align-items: start; font-size: 13px;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
  &__panel { display: grid; gap: 10px; }
  &__card { background: #fff; border-radius: 14px; padding: 12px 14px; box-shadow: $shadow; }
  &__head { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
  &__step { width: 22px; height: 22px; border-radius: 50%; background: #282828; color: #fff; font-size: 11px; font-weight: 800; display: inline-flex; align-items: center; justify-content: center; }
  &__muted { color: rgba(0, 0, 0, 0.5); font-size: 12px; }
  &__error { color: #c62828; font-size: 12px; font-weight: 600; }
  &__mono { font-family: ui-monospace, Menlo, monospace; font-size: 11px; }
  &__drop { border: 2px dashed rgba(0, 0, 0, 0.15); border-radius: 12px; min-height: 140px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; padding: 10px; cursor: pointer;
    &--over { border-color: #7b5cd6; background: #f3f0fc; }
  }
  &__artPreview { max-width: 100%; max-height: 220px; border-radius: 8px; }
  &__thumbs { display: flex; flex-wrap: wrap; gap: 8px;
    &--drop { align-items: center; padding: 4px; margin: -4px; border-radius: 12px; border: 2px dashed transparent; }
    &--drop.ul__drop--over { border-color: #7b5cd6; background: #f3f0fc; }
  }
  &__dropHint { margin-left: 4px; }
  &__thumb { position: relative; width: 72px; height: 72px; border-radius: 10px; overflow: hidden; background: #eee; img { width: 100%; height: 100%; object-fit: cover; } }
  &__thumbX { position: absolute; top: 2px; right: 2px; width: 20px; height: 20px; border-radius: 50%; border: 0; background: rgba(0, 0, 0, 0.55); color: #fff; cursor: pointer; line-height: 18px; }
  &__thumbAdd { width: 72px; height: 72px; border-radius: 10px; border: 2px dashed rgba(0, 0, 0, 0.15); background: none; cursor: pointer; }
  &__row { display: flex; gap: 8px; align-items: center; }
  &__select { max-width: 260px; &--s { max-width: 220px; } }
  &__chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
  &__chip { background: #f1f2f6; border-radius: 999px; padding: 2px 9px; font-size: 11.5px; }
  &__steps { display: grid; gap: 6px; }
  &__stepRow { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; min-height: 38px; }
  &__switch { margin: 0; padding: 0; min-width: 190px; }
  &__seg { display: inline-flex; background: #f1f2f6; border-radius: 999px; padding: 2px;
    button { border: 0; background: none; padding: 4px 12px; border-radius: 999px; font-size: 12px; font-weight: 600; color: rgba(0, 0, 0, 0.55); cursor: pointer; }
    button.on { background: #fff; color: #282828; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12); }
  }
  &__results { display: grid; gap: 10px; }
  &__empty { background: #fff; border-radius: 14px; box-shadow: $shadow; min-height: 200px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; }
  &__result { background: #fff; border-radius: 14px; box-shadow: $shadow; padding: 12px 14px; }
  &__resultHead { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  &__iconBtn { border: 0; background: none; cursor: pointer; }
}
</style>
